import { useEffect, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { isOmnichannelProxyMediaUrl } from '@/lib/helpers/omnichannel-media';
import { axiosInstance } from '@/lib/utils';

function parseProxyMediaParams(
  mediaUrl: string
): { content: string; conversationId: string } | null {
  try {
    const url = new URL(mediaUrl);
    const marker = '/omnichannel/media/';
    const idx = url.pathname.indexOf(marker);
    if (idx === -1) return null;

    const content = decodeURIComponent(url.pathname.slice(idx + marker.length));
    const conversationId = url.searchParams.get('conversationId') ?? '';
    if (!content || !conversationId) return null;

    return { content, conversationId };
  } catch {
    return null;
  }
}

/**
 * WhatsApp media must load via the authenticated proxy (Bearer token on axios).
 * Meta channels use direct URLs in `content` — returned as-is.
 */
export function useOmnichannelMediaUrl(mediaUrl: string | undefined) {
  const [displayUrl, setDisplayUrl] = useState<string | undefined>(mediaUrl);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);

    if (!mediaUrl?.trim()) {
      setDisplayUrl(undefined);
      return;
    }

    if (!isOmnichannelProxyMediaUrl(mediaUrl)) {
      setDisplayUrl(mediaUrl);
      return;
    }

    const params = parseProxyMediaParams(mediaUrl);
    if (!params) {
      setDisplayUrl(undefined);
      setFailed(true);
      return;
    }

    let objectUrl: string | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const path = `${endpoints.omnichannel.media}/${encodeURIComponent(params.content)}?conversationId=${encodeURIComponent(params.conversationId)}`;
        const response = await axiosInstance.get<Blob>(path, {
          responseType: 'blob',
        });
        objectUrl = URL.createObjectURL(response.data);
        if (!cancelled) {
          setDisplayUrl(objectUrl);
          setFailed(false);
        }
      } catch {
        if (!cancelled) {
          setDisplayUrl(undefined);
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaUrl]);

  return { displayUrl, failed };
}
